import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import Separator from 'flarum/common/components/Separator';
import ThemeMode from 'flarum/common/components/ThemeMode';
import ThemeSwitcher from 'flarum/forum/components/ThemeSwitcher';
import Link from 'flarum/common/components/Link';
import WelcomeHero from 'flarum/forum/components/WelcomeHero';
import IndexPage from 'flarum/forum/components/IndexPage';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import Badge from 'flarum/common/components/Badge';
import Tooltip from 'flarum/common/components/Tooltip';
import Icon from 'flarum/common/components/Icon';
import TagHero from 'ext:flarum/tags/forum/components/TagHero';
const text = key => app.translator.trans('bydtalk.forum.' + key);
app.initializers.add('bydtalk-brand', () => {
  extend(TagHero.prototype, 'contentItems', function (items) {
    if (this.attrs.model.slug() !== 'diplay') return;
    items.add('btDownloads', m("div", {
      className: "BydTalkTagDownloads"
    }, m("a", {
      className: "Button",
      href: "https://diplay.org/"
    }, m(Icon, {
      name: "fas fa-download"
    }), m("span", null, text('diplay_downloads')))), 80);
  });

  // Keep native tooltip/accessibility behavior; status labels are shown by context.
  extend(Badge.prototype, 'view', function (vnode) {
    if (!this.attrs.label) return;
    const badge = vnode.tag === Tooltip ? vnode.children[0] : vnode;
    badge.children.push(m("span", {
      className: "Badge-label",
      "aria-hidden": "true"
    }, this.attrs.label));
  });
  extend(DiscussionListItem.prototype, 'authorItems', items => items.remove('badges'));
  extend(DiscussionListItem.prototype, 'mainView', function (vnode) {
    const badges = this.badgesView();
    if (!badges.children.length) return;
    vnode.children[0] = m("div", {
      className: "BydTalkDiscussionHeading"
    }, vnode.children[0], badges);
  });
  const modes = ['light', 'dark', 'auto'];
  const icons = ['fa-regular fa-sun', 'fa-regular fa-moon', 'fa-solid fa-desktop'];
  ThemeMode.colorSchemes = ThemeMode.colorSchemes.filter(scheme => modes.includes(scheme.id));
  override(ThemeSwitcher.prototype, 'view', function () {
    const active = this.active().replace('-hc', '');
    const index = Math.max(0, modes.indexOf(active));
    const next = modes[(index + 1) % modes.length];
    const label = app.translator.trans('bydtalk.forum.theme_cycle', {
      current: this.label(modes[index]),
      next: this.label(next)
    }, true);
    return m(Button, {
      className: "Button Button--flat Button--icon BydTalkThemeCycle",
      icon: icons[index],
      "aria-label": label,
      title: label,
      onclick: () => this.select(next)
    });
  });
  extend(DiscussionListItem.prototype, 'infoItems', function (items) {
    const excerpt = this.attrs.discussion.attribute('firstPostExcerpt');
    if (!this.attrs.params.q && excerpt && !items.has('excerpt')) {
      items.add('excerpt', m("span", null, Array.from(excerpt).slice(0, 175).join('')), -90);
    }
  });
  extend(HeaderPrimary.prototype, 'items', function (items) {
    items.add('btDiscussions', m(Link, {
      className: "BydTalkHeaderLink",
      href: app.route('index')
    }, text('discussions')), 20);
    items.add('btTags', m(Link, {
      className: "BydTalkHeaderLink",
      href: app.route('tags')
    }, text('all_tags')), 10);
  });
  extend(WelcomeHero.prototype, 'bodyItems', items => {
    items.add('btSignature', m("span", {
      className: "BydTalkHeroSignature",
      "aria-hidden": "true"
    }, m("img", {
      className: "BydTalkSignatureLight",
      src: app.forum.attribute('baseUrl') + '/brand/hero-signature-v2.webp',
      alt: ""
    }), m("img", {
      className: "BydTalkSignatureDark",
      src: app.forum.attribute('baseUrl') + '/brand/hero-signature-dark-v2.webp',
      alt: ""
    })), 70);
  });
  extend(WelcomeHero.prototype, 'contentItems', function (items) {
    items.add('btActions', m("div", {
      className: "BydTalkHeroActions"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => IndexSidebar.prototype.newDiscussionAction().catch(() => {})
    }, text('join_conversation')), m(Link, {
      className: "Button BydTalkOutline",
      href: app.route('models')
    }, text('explore_models'))), -10);
  });
  extend(IndexPage.prototype, 'viewItems', function (items) {
    const sort = items.get('sort');
    const actions = this.actionItems();
    actions.remove('search');
    for (const key of ['refresh', 'markAllAsRead']) {
      if (!actions.has(key)) continue;
      const button = actions.get(key);
      actions.setContent(key, m(button.tag, {
        ...button.attrs,
        className: ''
      }, button.attrs['aria-label']));
    }
    sort.attrs.className = 'BydTalkIndexSort';
    sort.attrs.accessibleToggleLabel = text('list_options');
    sort.children.push(m(Separator, null), ...actions.toArray().map(button => m(button.tag, button.attrs, button.children)));
  });
  extend(IndexPage.prototype, 'toolbarItems', function (items) {
    const current = app.currentTag?.();
    const params = app.search.state.params();
    const all = !current && !params.q && !Object.keys(params.filter || {}).length;
    const slugs = ['news', 'buying', 'charging', 'ownership', 'troubleshooting'];
    const tags = slugs.map(slug => app.store.all('tags').find(tag => tag.slug() === slug)).filter(Boolean);
    items.add('btTopicBar', m("nav", {
      className: "BydTalkTopicBar",
      "aria-label": text('browse_topics')
    }, m(Link, {
      className: 'BydTalkTopic' + (all ? ' is-active' : ''),
      href: app.route('index'),
      "aria-current": all ? 'page' : undefined
    }, text('all_topics')), tags.map(tag => m(Link, {
      className: 'BydTalkTopic' + (current?.id() === tag.id() ? ' is-active' : ''),
      href: app.route.tag(tag),
      "aria-current": current?.id() === tag.id() ? 'page' : undefined
    }, tag.name())), m(Link, {
      className: "BydTalkTopic BydTalkTopic--more",
      href: app.route('tags')
    }, text('all_tags'))), 110);
    items.remove('action');
  });
});